import { AuthFacade } from 'src/app/store/auth/facade/auth.facade';
import { ICurrentRebalance, IPortfolio, IPortfolioStatus, ITransferCashCommand } from 'src/app/models/portfolios';
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import {tap, filter, take, retry, map} from 'rxjs/operators';
import * as portfolioQuery from 'src/app/store/portfolios/selectors/portfolios.selectors';
import {
  SearchPortfolios,
  GetInvestorPortfolios,
  LoadPortfolioEntity,
  SelectPortfolioEntityPosition,
  GetReferenceNumber,
  LoadPortfoliosForEntities
} from 'src/app/store/portfolios/actions/portfolios.actions';
import { IPortfolioPosition, IPortfolioDataEntity } from 'src/app/models/portfolios';
import { IAppState } from 'src/app/store/state/app.state';
import {PortfoliosService} from "../../../services/http/portfolios.service";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GeneralSelectionFacade } from '../../general-selection/facade/general-selection.facade';
import {IEntity} from "../../client/reducers/client.reducers";

@UntilDestroy()
@Injectable()
export class PortfoliosFacade {
  selectedPortfolioEntity$: Observable<IPortfolioDataEntity>;
  private availablePortfolios$: Observable<IPortfolio[]> = this.store.pipe(select(portfolioQuery.getAvailablePortfolios));
  private isUserLoggedIn$: Observable<boolean> = this.authFacade.userLoggedIn$();
  private portfolioStateLoading$: Observable<boolean> = this.store.pipe(select(portfolioQuery.getPortfolioLoadingState));

  private portfolioStateLoading: boolean = false;

  constructor(
    private store: Store<IAppState>, 
    private authFacade: AuthFacade, 
    private portfoliosService: PortfoliosService,
    private generalSelectionFacade: GeneralSelectionFacade
  ) {
    this.selectedPortfolioEntity$ = store.pipe(select(portfolioQuery.getSelectedPortfolioEntity));
    this.isLoading();
  }

    public portfoliosState$ = this.store.pipe(
        select(portfolioQuery.getPortfoliosState)
    )

    public getInvestorPortfolios() {
      if (!this.portfolioStateLoading) {
        this.store.dispatch(new GetInvestorPortfolios());
      }
    }

  //When possible avoid using getAvailablePortfolios and use getEntitiesPortfolios instead.
  //getEntitiesPortfolios retrieves portfolios from all sources in the state.
    public getAvailablePortfolios(): Observable<IPortfolio[]> {
        return combineLatest([
            this.availablePortfolios$,
            this.isUserLoggedIn$
        ]).pipe(
            filter((values: [IPortfolio[], boolean]) => {
                const isLoggedIn = values[1];
                return isLoggedIn;
            }),
            map((values: [IPortfolio[], boolean]) => {
                const availablePortfolios = values[0];
                return availablePortfolios;
            }),
            tap((portfolios: IPortfolio[]) => {
                if (!portfolios) {
                    this.getInvestorPortfolios();
                }
            })
        );
    }

  // Get all loaded portfolios from the store
  public isLoading() {
    this.portfolioStateLoading$
      .pipe(untilDestroyed(this))
      .subscribe((isLoading) => {
        this.portfolioStateLoading = isLoading;
      })
    }

    /**
     * Returns a collection of portfolios gathered from all areas of state which hold portfolios.
     * Note that this call does not respect *selected* entities in the state i.e. if a user has access to three entities with three portfolios and then 
     * deselected an entity this call does not refresh, so anything subscribing to it would still return portfolios for all three *available* entities.
     */
    public getEntitiesPortfolios(): Observable<IPortfolio[]> {
      return combineLatest([
        this.store.pipe(select(portfolioQuery.getAvailablePortfolios)),
        this.store.pipe(select(portfolioQuery.getEntitiesPortfolios))
      ]).pipe(
          map((
            [
              investorPortfolios,
              entitiesPortfolios
            ]
          ) => {

            // hack to fix prod issue with RR where an administrator
            // who is also part of trust is showing their investor
            // portfolio on all screens. trying here to cut it off at the
            // knees instead of sprinkling administrator checks everywhere
            // to filter out the investor portfolio.
            if (this.authFacade.isAdministrator()) {
              investorPortfolios = [];
            }

            const combinedPortfolios = [
              ...(investorPortfolios || []),
              ...(entitiesPortfolios || [])
            ];

            const distinctPortfolios: IPortfolio[] = [];

            // Add unique portfolios to remove potential duplicates.
            combinedPortfolios.forEach(p => {
              if (!distinctPortfolios.find(dp => dp.id === p.id)) {
                distinctPortfolios.push(p);
              }
            });

            return distinctPortfolios;
          }
        ));
    }

    public loadPortfoliosForEntities(entityIds: number[]) {
      if (!entityIds || entityIds.length === 0)
        return;

      this.store.dispatch(new LoadPortfoliosForEntities(entityIds));
    }

    public getPortfoliosForEntities() : Observable<IPortfolio[]> {
      return this.portfoliosService.getInvestorPortfolios();
    }

    public getPortfolioEntity(portfolioId: number) {
        this.store.dispatch(new LoadPortfolioEntity(portfolioId));
    }
    public selectPortfolioEntityPosition(position: IPortfolioPosition) {
        this.store.dispatch(new SelectPortfolioEntityPosition(position));
    }

    public searchPortfolios(query: string) {
        this.store.dispatch(new SearchPortfolios(query));
    }

    public getInvestorReferenceNumber(investorId: number) {
        return this.store.pipe(
            select(portfolioQuery.getReferenceNumbers),
            map(referenceNumbers => {
                return referenceNumbers[investorId];
            }),
            tap(referenceNumber => {
                if (referenceNumber === undefined) {
                    this.store.dispatch(new GetReferenceNumber(investorId));
                }
            }),
            filter(referenceNumber => {
                return referenceNumber != null;
            }),
            take(1)
        );
    }

  public cancelRebalance(portfolioId: number, currentRebalance: ICurrentRebalance): Observable<any>{
    return this.portfoliosService.cancelRebalance(portfolioId, currentRebalance);
  }

    // Dispatches a request to get portfolios net asset value data.
    public getPortfoliosNetAssets(portfolioIds: number[], selectedDate: Date) : void{
      this.portfoliosService.getPortfoliosNetAssets(portfolioIds, selectedDate);
    }

  //
  // transfer cash
  public transferCash(transferCashCommand : ITransferCashCommand) {
    return this.portfoliosService.transferCash(transferCashCommand);
  }

  public getPortfolioStatus(portfolioId: number): Observable<IPortfolioStatus>{
    return this.portfoliosService.getPortfolioStatus(portfolioId);
  }

  /**
   * Retrieves the portfolios of the selected entities. 
   * This is required because there is nowhere in the state that universally respects the portfolios of selected entities.
   */
  public getPortfoliosOfSelectedEntities(): Observable<IPortfolio[]> {
    return combineLatest([
      this.getEntitiesPortfolios(),
      this.generalSelectionFacade.getSelectedInvestorEntities()
    ])
    .pipe(
      map(([availablePortfolios, selectedEntities]) => {

      let selectedEntityIds: number[] = [];
      let selectedPortfolios: IPortfolio[] = [];

      //Get the entity ids of selected entities
      if (selectedEntities !== undefined && selectedEntities !== null && selectedEntities.length > 0) {
        let ids = selectedEntities.map(x => x.EntityId)
        ids.forEach((x) => {
          selectedEntityIds.push(x);
        })
      }

      //Now check in the available portfolios and only pull the portfolios for the selected entities
      if (availablePortfolios !== null && availablePortfolios !== undefined && availablePortfolios.length > 0) {
        let filteredPortfolios = availablePortfolios.filter(x => selectedEntityIds.includes(x.entityId));
        filteredPortfolios.forEach((portfolio) => {
          selectedPortfolios.push(portfolio);
        });
      }

      return selectedPortfolios;
    })
  )}
  
  public getPortfoliosOfSelectedClient(): Observable<IPortfolio[]> {
      return combineLatest([
          this.getEntitiesPortfolios(),
          this.generalSelectionFacade.getSelectedClient()
      ])
          .pipe(map(([availablePortfolios, selectedClient]) => {
              let selectedEntityIds: number[] = [];
              let selectedPortfolios: IPortfolio[] = [];
              
              if(!selectedClient)
                  return;
              
              const members: IEntity[] = selectedClient.Members
              
              let ids = members.map(x => x.EntityId);
                ids.forEach((x) => {
                    selectedEntityIds.push(x);
                })
              
              // Client must be an individual investor if it has no members
              if (ids.length == 0) {
                  selectedEntityIds.push(selectedClient.EntityId);
              }

              if (availablePortfolios !== null && availablePortfolios !== undefined && availablePortfolios.length > 0) {
                  let filteredPortfolios = availablePortfolios.filter(x => selectedEntityIds.includes(x.entityId));
                  filteredPortfolios.forEach((portfolio) => {
                      selectedPortfolios.push(portfolio);
                  });
              }

              return selectedPortfolios;
              })
          )
  }
}
